import { Sentry, initSentry, setSentryUser, clearSentryUser } from '@thistle/mainframe-sentry';
import './style.css';
import { router } from './router/router.js';
import { dataService } from './services/dataService.js';
import { barcodeScanner } from './services/barcodeScanner.js';
import { authService } from '@thistle/mainframe-auth';
import { dataRefreshService } from './services/dataRefreshService.js';
import { ENV } from './config/environment.js';

// Import all components to register them
import './components/PackStatusHeader.js';
import './components/NavigationMenu.js';
import './components/PackLinesSnapshot.js';
import './components/RoutesList.js';
import './components/PackLinesDrawer.js';
import './components/Alert.js';
import './components/LoadingSpinner.js';
import './components/LoadingOverlay.js';
import '@thistle/mainframe-auth/vanillaAuthOverlay';
import { appStore } from './store/appStore.js';

// Initialize the application
async function initApp() {
  initSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    appName: 'bob-frontend',
  });

  try {
    document.addEventListener('thistle-signed-in', (event) => {
      appStore.setState(state => ({ ...state, isAuthenticated: true }));
    })
    document.addEventListener('thistle-signed-out', (event) => {
      appStore.setState(state => ({ ...state, isAuthenticated: false }));
    })

    authService.configure(ENV.mainframeTokenIssuerUrl);
    await authService.initializeAuth();

    // Set up auth state listener to initialize/cleanup services
    let lastAuthState = appStore.getState().isAuthenticated;

    appStore.subscribe(async () => {
      const currentAuthState = appStore.getState().isAuthenticated;
      if (currentAuthState !== lastAuthState) {
        lastAuthState = currentAuthState;

        if (currentAuthState) {
          const user = authService.getUser();
          if (user) {
            setSentryUser({
              username: user.name,
              email: user.email,
              identityProvider: user.identityProvider,
            });
          }
          // Initialize data service and barcode scanner when authenticated
          try {
            await dataService.initialize({ force: true });
            barcodeScanner.initialize();
            dataRefreshService.start();
          } catch (error) {
            console.error('Failed to initialize authenticated services:', error);
          }
        } else {
          clearSentryUser();
          dataRefreshService.stop();
        }
        // Note: We don't need to cleanup services on signout as they handle auth failures gracefully
      }
    });

    // Start router (it will handle authentication checks and trigger auth listener if needed)
    router.start();

    // Initialize data service immediately if already authenticated
    if (appStore.getState().isAuthenticated) {
      try {
        await dataService.initialize();
        barcodeScanner.initialize();
        dataRefreshService.start();
      } catch (error) {
        console.error('Failed to initialize authenticated services on startup:', error);
      }
    }

    console.log('BOB application initialized successfully');
  } catch (error) {
    console.error('Failed to initialize BOB application:', error);
    Sentry.captureException(error);

    // Show error message to user
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div class="min-h-screen bg-red-50 flex items-center justify-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-red-900 mb-2">Application Error</h1>
            <p class="text-red-700 mb-4">Failed to load the application. Please refresh the page.</p>
            <button
              onclick="location.reload()"
              class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            >
              Refresh Page
            </button>
          </div>
        </div>
      `;
    }
  }
}

// Handle browser back/forward navigation
window.addEventListener('popstate', () => {
  router.resolve();
});

// Start the app when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
